import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import type { CycleRing } from '@/lib/cycleTypes';
import { CycleAlignment } from '@/lib/cycleConfig';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Input } from '@/components/ui/input';
import { Link2, ArrowRight, Info } from 'lucide-react';

interface AlignmentToolProps {
  open: boolean;
  onClose: () => void;
  cycles: CycleRing[];
  onAlign: (alignment: CycleAlignment) => void;
  onApplyAlignment: (cycles: CycleRing[]) => void;
  alignments?: CycleAlignment[];
  initialSourceCycleId?: string;
  timelineDate?: Date | null;
}

const SYNODIC_MONTH = 29.53059;
const LUNAR_STAGES = 30;
const TIMELINE_CYCLE_ID = '__timeline__';

const getTimelineStages = (date: Date) => {
  const year = date.getFullYear();
  const isLeapYear = new Date(year, 1, 29).getMonth() === 1;
  return isLeapYear ? 366 : 365;
};

const buildTimelineCycle = (date: Date): CycleRing => {
  const stages = getTimelineStages(date);
  return {
    id: TIMELINE_CYCLE_ID,
    name: 'Timeline',
    stages,
    labels: Array.from({ length: stages }, (_, i) => String(i + 1)),
    colors: Array.from({ length: stages }, () => '210 8% 62%'),
  };
};

type CycleRelationshipRule = {
  description: string;
  sourceSegments: number;
  targetSegments: number;
  detail: string;
};

// Preset relationship explanations
const CYCLE_RELATIONSHIPS: Record<string, CycleRelationshipRule> = {
  'days-moons': {
    description: '28 Days completes 1 Moon',
    sourceSegments: 28,
    targetSegments: 1,
    detail: 'Each Moon has exactly 28 days. When the Days ring completes one full rotation, the Moons ring advances by one stage.',
  },
  'moons-lunar': {
    description: 'Moons and Lunar drift over time',
    sourceSegments: 28 * LUNAR_STAGES,
    targetSegments: SYNODIC_MONTH,
    detail: 'The 13-Moon calendar uses 28-day months, while the actual lunar cycle is ~29.53 days. They naturally drift apart.',
  },
  'days-lunar': {
    description: '28 Days ≈ 1 Lunar cycle',
    sourceSegments: LUNAR_STAGES,
    targetSegments: SYNODIC_MONTH,
    detail: 'The Days ring (28 stages) approximates one lunar month, but the real Moon takes ~29.53 days.',
  },
  'solarSecond-solarMinute': {
    description: '60 Seconds = 1 Minute',
    sourceSegments: 60,
    targetSegments: 1,
    detail: 'Standard time: 60 seconds make one minute.',
  },
  'solarMinute-solarHour': {
    description: '60 Minutes = 1 Hour',
    sourceSegments: 60,
    targetSegments: 1,
    detail: 'Standard time: 60 minutes make one hour.',
  },
  'solarHour-sun': {
    description: '24 Hours = 1 Solar Day',
    sourceSegments: 24,
    targetSegments: 1,
    detail: 'When Solar Hour completes one rotation (24 hours), one Solar Day passes.',
  },
  'sun-days': {
    description: '1 Solar Day = 1 Calendar Day',
    sourceSegments: 1,
    targetSegments: 1,
    detail: 'Each Solar Day advances the 28-day calendar by one day.',
  },
};

function getRelationshipKey(id1: string, id2: string): string | null {
  const keys = [`${id1}-${id2}`, `${id2}-${id1}`];
  for (const key of keys) {
    if (CYCLE_RELATIONSHIPS[key]) return key;
  }
  return null;
}

function simplifyRatio(source: number, target: number) {
  if (source <= 0 || target <= 0) return { source, target };
  const gcd = (a: number, b: number): number => b === 0 ? a : gcd(b, a % b);
  const divisor = gcd(Math.round(source * 10000), Math.round(target * 10000));
  return {
    source: source / divisor,
    target: target / divisor,
  };
}

export function AlignmentTool({
  open,
  onClose,
  cycles,
  onAlign,
  onApplyAlignment,
  alignments = [],
  initialSourceCycleId,
  timelineDate,
}: AlignmentToolProps) {
  const [sourceCycleId, setSourceCycleId] = useState<string>('');
  const [targetCycleId, setTargetCycleId] = useState<string>('');
  const [sourceRatio, setSourceRatio] = useState<number>(1);
  const [targetRatio, setTargetRatio] = useState<number>(1);
  const [sourceStage, setSourceStage] = useState<number>(1);
  const [targetStage, setTargetStage] = useState<number>(1);
  const [sourceWaveform, setSourceWaveform] = useState<CycleRing['waveform']>('default');
  const lastAppliedKeyRef = useRef<string | null>(null);

  const timelineCycle = useMemo(
    () => buildTimelineCycle(timelineDate ?? new Date()),
    [timelineDate],
  );
  const sourceCycle = cycles.find(c => c.id === sourceCycleId);
  const targetCycle = targetCycleId === TIMELINE_CYCLE_ID
    ? timelineCycle
    : cycles.find(c => c.id === targetCycleId);

  const getRuleRatio = useCallback(() => {
    if (!sourceCycleId || !targetCycleId) return null;
    const key = getRelationshipKey(sourceCycleId, targetCycleId);
    if (!key) return null;
    const relationship = CYCLE_RELATIONSHIPS[key];
    if (!relationship) return null;

    if (key.startsWith(sourceCycleId)) {
      return { source: relationship.sourceSegments, target: relationship.targetSegments };
    }
    return { source: relationship.targetSegments, target: relationship.sourceSegments };
  }, [sourceCycleId, targetCycleId]);

  const getExistingAlignment = useCallback(() => {
    if (!sourceCycleId || !targetCycleId) return null;
    return alignments.find(
      (alignment) =>
        (alignment.sourceCycleId === sourceCycleId && alignment.targetCycleId === targetCycleId) ||
        (alignment.sourceCycleId === targetCycleId && alignment.targetCycleId === sourceCycleId)
    );
  }, [alignments, sourceCycleId, targetCycleId]);

  const clampStageValue = (value: number, stages: number) => {
    if (stages <= 0) return 1;
    return Math.min(Math.max(Math.round(value), 1), stages);
  };

  // Calculate current ratio between cycles
  const getCurrentRatio = useCallback(() => {
    if (!sourceCycle || !targetCycle) return null;
    
    // If an alignment already exists, respect its stored segment ratio first
    const existingAlignment = getExistingAlignment();

    if (
      existingAlignment &&
      existingAlignment.sourceSegments &&
      existingAlignment.targetSegments
    ) {
      const isForward = existingAlignment.sourceCycleId === sourceCycleId;
      return {
        source: isForward ? existingAlignment.sourceSegments : existingAlignment.targetSegments,
        target: isForward ? existingAlignment.targetSegments : existingAlignment.sourceSegments,
      };
    }
    
    // Check if directly linked
    if (sourceCycle.parentId === targetCycle.id && sourceCycle.stagesPerParent) {
      return { source: sourceCycle.stagesPerParent, target: 1 };
    }
    if (targetCycle.parentId === sourceCycle.id && targetCycle.stagesPerParent) {
      return { source: 1, target: targetCycle.stagesPerParent };
    }
    
    // Check preset relationships
    const ruleRatio = getRuleRatio();
    if (ruleRatio) {
      return ruleRatio;
    }
    
    // Default to using segment counts so ratios always reflect how many segments
    // advance relative to each other (ratio is by segment, not by full rotation)
    return simplifyRatio(sourceCycle.stages, targetCycle.stages);
  }, [getExistingAlignment, getRuleRatio, sourceCycle, sourceCycleId, targetCycle]);

  // Preload with moons and lunar as example
  useEffect(() => {
    if (open) {
      if (initialSourceCycleId) {
        setSourceCycleId(initialSourceCycleId);
        const sourceCycle = cycles.find(c => c.id === initialSourceCycleId);
        if (sourceCycle?.parentId) {
          if (sourceCycle.parentId === TIMELINE_CYCLE_ID) {
            setTargetCycleId(TIMELINE_CYCLE_ID);
          } else {
            const parentCycle = cycles.find(c => c.id === sourceCycle.parentId);
            if (parentCycle) {
              setTargetCycleId(parentCycle.id);
            }
          }
        }
      } else {
        // Default to showing moons ↔ lunar relationship
        const moonsRing = cycles.find(c => c.id === 'moons');
        const lunarRing = cycles.find(c => c.id === 'lunar');
        if (moonsRing && lunarRing) {
          setSourceCycleId('moons');
          setTargetCycleId('lunar');
        }
      }
    }
  }, [open, initialSourceCycleId, cycles]);

  // Update ratio when cycles change
  useEffect(() => {
    const ratio = getCurrentRatio();
    if (ratio) {
      setSourceRatio(ratio.source);
      setTargetRatio(ratio.target);
    }
  }, [getCurrentRatio]);

  useEffect(() => {
    setSourceWaveform(sourceCycle?.waveform ?? 'default');
  }, [sourceCycle?.waveform, sourceCycleId]);

  useEffect(() => {
    if (!sourceCycle || !targetCycle) return;
    const existingAlignment = getExistingAlignment();
    if (existingAlignment) {
      const isForward = existingAlignment.sourceCycleId === sourceCycleId;
      const sourceStageValue = isForward ? existingAlignment.sourceStage : existingAlignment.targetStage;
      const targetStageValue = isForward ? existingAlignment.targetStage : existingAlignment.sourceStage;
      setSourceStage(clampStageValue(sourceStageValue + 1, sourceCycle.stages));
      setTargetStage(clampStageValue(targetStageValue + 1, targetCycle.stages));
      return;
    }
    setSourceStage(clampStageValue(1, sourceCycle.stages));
    setTargetStage(clampStageValue(1, targetCycle.stages));
  }, [getExistingAlignment, sourceCycle, sourceCycleId, targetCycle]);

  const relationshipKey = sourceCycleId && targetCycleId ? getRelationshipKey(sourceCycleId, targetCycleId) : null;
  const relationship = relationshipKey ? CYCLE_RELATIONSHIPS[relationshipKey] : null;
  const ruleRatio = getRuleRatio();

  // Build hierarchy chain for a cycle
  const getHierarchyChain = (cycle: CycleRing): CycleRing[] => {
    const chain: CycleRing[] = [cycle];
    let current = cycle;
    while (current.parentId) {
      const parent = cycles.find(c => c.id === current.parentId);
      if (parent) {
        chain.push(parent);
        current = parent;
      } else {
        break;
      }
    }
    return chain;
  };

  const handleQuickAlign = (mode: 'all-top' | 'cascade') => {
    let newCycles: CycleRing[];
    
    if (mode === 'all-top') {
      newCycles = cycles.map(c => ({ ...c, rotation: 0 }));
    } else {
      newCycles = cycles.map((c, idx) => {
        const segmentAngle = 360 / c.stages;
        return { ...c, rotation: -idx * segmentAngle };
      });
    }
    
    onApplyAlignment(newCycles);
    onClose();
  };

  const applyRelationship = useCallback(() => {
    if (!sourceCycle || !targetCycle) return;

    const sourceStageIndex = clampStageValue(sourceStage, sourceCycle.stages) - 1;
    const targetStageIndex = clampStageValue(targetStage, targetCycle.stages) - 1;
    
    // Update stagesPerParent based on the new ratio
    const newCycles = cycles.map(c => {
      if (c.id === sourceCycleId && targetCycleId === TIMELINE_CYCLE_ID) {
        return {
          ...c,
          parentId: TIMELINE_CYCLE_ID,
          stagesPerParent: sourceRatio / targetRatio,
          waveform: sourceWaveform ?? 'default',
          waveformIntervalMultiplier: sourceCycle.stages,
          waveformIntervalDivider: 1,
        };
      }
      if (c.id === sourceCycleId && c.parentId === targetCycleId) {
        return {
          ...c,
          stagesPerParent: sourceRatio / targetRatio,
          waveform: sourceWaveform ?? 'default',
          waveformIntervalMultiplier: sourceCycle.stages,
          waveformIntervalDivider: 1,
        };
      }
      if (c.id === targetCycleId && targetCycleId !== TIMELINE_CYCLE_ID && targetCycle.parentId === sourceCycleId) {
        return { ...c, stagesPerParent: targetRatio / sourceRatio };
      }
      if (c.id === sourceCycleId) {
        const segmentAngle = 360 / sourceCycle.stages;
        return {
          ...c,
          rotation: -sourceStageIndex * segmentAngle,
          waveform: sourceWaveform ?? 'default',
          waveformIntervalMultiplier: sourceCycle.stages,
          waveformIntervalDivider: 1,
        };
      }
      if (c.id === targetCycleId && targetCycleId !== TIMELINE_CYCLE_ID) {
        const segmentAngle = 360 / targetCycle.stages;
        return { ...c, rotation: -targetStageIndex * segmentAngle };
      }
      return c;
    });
    
    onApplyAlignment(newCycles);
    
    const alignment: CycleAlignment = {
      sourceCycleId,
      targetCycleId,
      sourceStage: sourceStageIndex,
      targetStage: targetStageIndex,
      sourceSegments: sourceRatio,
      targetSegments: targetRatio,
      name: `${(sourceCycle?.name ?? 'Unnamed')} ${sourceRatio}:${targetRatio} ${(targetCycle?.name ?? 'Unnamed')}`,
    };
    onAlign(alignment);
  }, [
    clampStageValue,
    cycles,
    onAlign,
    onApplyAlignment,
    sourceCycle,
    sourceCycleId,
    sourceRatio,
    sourceStage,
    sourceWaveform,
    targetCycle,
    targetCycleId,
    targetRatio,
    targetStage,
  ]);

  useEffect(() => {
    if (!open || !sourceCycle || !targetCycle) return;
    const payloadKey = JSON.stringify({
      sourceCycleId,
      targetCycleId,
      sourceRatio,
      targetRatio,
      sourceStage,
      targetStage,
      sourceWaveform,
    });
    if (lastAppliedKeyRef.current === payloadKey) return;
    lastAppliedKeyRef.current = payloadKey;
    applyRelationship();
  }, [
    open,
    sourceCycle,
    sourceCycleId,
    sourceRatio,
    sourceStage,
    sourceWaveform,
    targetCycle,
    targetCycleId,
    targetRatio,
    targetStage,
    applyRelationship,
  ]);

  const resetForm = () => {
    setSourceCycleId('');
    setTargetCycleId('');
    setSourceRatio(1);
    setTargetRatio(1);
    setSourceStage(1);
    setTargetStage(1);
    setSourceWaveform('default');
    lastAppliedKeyRef.current = null;
  };

  const ratioPerTarget = targetRatio > 0 ? sourceRatio / targetRatio : null;
  const ratioPerSource = sourceRatio > 0 ? targetRatio / sourceRatio : null;

  const getDirectLinkSummary = () => {
    if (!sourceCycle || !targetCycle) return null;

    if (sourceCycle.parentId === targetCycle.id && sourceCycle.stagesPerParent) {
      return {
        title: 'Direct tether',
        description: `1 ${(targetCycle?.name ?? 'Unnamed')} segment = ${sourceCycle.stagesPerParent} ${(sourceCycle?.name ?? 'Unnamed')} segments.`,
      };
    }

    if (targetCycle.parentId === sourceCycle.id && targetCycle.stagesPerParent) {
      return {
        title: 'Direct tether',
        description: `1 ${(sourceCycle?.name ?? 'Unnamed')} segment = ${targetCycle.stagesPerParent} ${(targetCycle?.name ?? 'Unnamed')} segments.`,
      };
    }

    return null;
  };

  const directLinkSummary = getDirectLinkSummary();

  return (
    <Dialog open={open} onOpenChange={() => { onClose(); resetForm(); }}>
      <DialogContent className="sm:max-w-lg bg-card border-border max-h-[80vh] flex flex-col">
        <DialogHeader>
          <DialogTitle className="font-display flex items-center gap-2">
            <Link2 className="w-5 h-5" />
            Cycle Relationships
          </DialogTitle>
        </DialogHeader>
        
        <div className="space-y-6 py-4 overflow-y-auto pr-1">
          {/* Quick Align Options */}
          <div className="space-y-2">
            <Label className="text-xs text-muted-foreground uppercase tracking-wider">Quick Actions</Label>
            <div className="flex gap-2">
              <Button variant="outline" size="sm" onClick={() => handleQuickAlign('all-top')} className="flex-1">
                Reset All to Start
              </Button>
              <Button variant="outline" size="sm" onClick={() => handleQuickAlign('cascade')} className="flex-1">
                Cascade Offset
              </Button>
            </div>
          </div>
          
          <div className="border-t border-border pt-4">
            <Label className="text-xs text-muted-foreground uppercase tracking-wider">Relationship Builder</Label>
          </div>
          
          {/* Cycle Selection */}
          <div className="flex items-center gap-3">
            <div className="flex-1 space-y-2">
              <Label>First Cycle</Label>
              <Select value={sourceCycleId} onValueChange={setSourceCycleId}>
                <SelectTrigger className="bg-background">
                  <SelectValue placeholder="Select cycle" />
                </SelectTrigger>
                <SelectContent className="bg-card border-border z-50">
                  {cycles.map(c => (
                    <SelectItem key={c.id} value={c.id}>
                      {(c?.name ?? 'Unnamed')} ({c.stages})
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            <ArrowRight className="w-5 h-5 text-muted-foreground mt-6" />
            
            <div className="flex-1 space-y-2">
              <Label>Second Cycle</Label>
              <Select value={targetCycleId} onValueChange={setTargetCycleId}>
                <SelectTrigger className="bg-background">
                  <SelectValue placeholder="Select cycle" />
                </SelectTrigger>
                <SelectContent className="bg-card border-border z-50">
                  <SelectItem key={TIMELINE_CYCLE_ID} value={TIMELINE_CYCLE_ID}>
                    Timeline (Gregorian day · {timelineCycle.stages} stages)
                  </SelectItem>
                  {cycles.filter(c => c.id !== sourceCycleId).map(c => (
                    <SelectItem key={c.id} value={c.id}>{(c?.name ?? 'Unnamed')} ({c.stages})</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Waveform selection for each cycle */}
          {sourceCycle && targetCycle && (
              <div className="rounded-lg border border-border bg-muted/15 p-4 space-y-3">
                <Label className="text-xs text-muted-foreground uppercase tracking-wider">Master Waveform</Label>
                <div className="space-y-1">
                  <Label className="text-xs text-muted-foreground">{sourceCycle.name}</Label>
                  <Select value={sourceWaveform ?? 'default'} onValueChange={(value) => setSourceWaveform(value as CycleRing['waveform'])}>
                    <SelectTrigger className="bg-background">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-card border-border z-50">
                      <SelectItem value="default">Default (linear)</SelectItem>
                      <SelectItem value="sine">Sine</SelectItem>
                      <SelectItem value="triangle">Triangle</SelectItem>
                      <SelectItem value="square">Square</SelectItem>
                      <SelectItem value="sawtooth">Sawtooth</SelectItem>
                      <SelectItem value="24hr">Legacy 24hr Sine</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <p className="text-xs text-muted-foreground">Waveform applies to the master cycle and will render one waveform loop per master segment.</p>
              </div>
          )}

          {/* Ratio Adjustment */}
          {sourceCycle && targetCycle && (
            <div className="rounded-lg border border-primary/30 bg-primary/5 p-4 space-y-3">
              <Label className="text-xs text-muted-foreground uppercase tracking-wider">Segment Ratio</Label>
              <div className="flex items-center gap-3">
                <div className="flex-1 space-y-1">
                  <Label className="text-xs text-muted-foreground">{sourceCycle.name}</Label>
                  <Input
                    type="number"
                    step="0.01"
                    min="0.01"
                    value={sourceRatio}
                    onChange={(e) => setSourceRatio(parseFloat(e.target.value) || 1)}
                    className="bg-background font-mono text-center"
                  />
                </div>
                <div className="text-2xl font-light text-muted-foreground">:</div>
                <div className="flex-1 space-y-1">
                  <Label className="text-xs text-muted-foreground">{targetCycle.name}</Label>
                  <Input
                    type="number"
                    step="0.01"
                    min="0.01"
                    value={targetRatio}
                    onChange={(e) => setTargetRatio(parseFloat(e.target.value) || 1)}
                    className="bg-background font-mono text-center"
                  />
                </div>
              </div>
              <div className="grid gap-1 text-center text-sm text-muted-foreground">
                {ratioPerTarget !== null && (
                  <div>
                    1 {targetCycle.name} segment ={' '}
                    <span className="font-mono text-primary">{ratioPerTarget.toFixed(4)}</span> {sourceCycle.name} segments
                  </div>
                )}
                {ratioPerSource !== null && (
                  <div>
                    1 {sourceCycle.name} segment ={' '}
                    <span className="font-mono text-primary">{ratioPerSource.toFixed(4)}</span> {targetCycle.name} segments
                  </div>
                )}
              </div>
              <div className="space-y-1 text-xs text-muted-foreground">
                <p>
                  This ratio is based on <span className="font-medium">segments</span>, not full rotations.
                </p>
                <p>
                  Enter how many segments advance together (per move/trigger). When {sourceCycle.name} advances{' '}
                  <span className="text-primary">{sourceRatio}</span> segment{sourceRatio !== 1 ? 's' : ''},{' '}
                  {targetCycle.name} advances <span className="text-primary">{targetRatio}</span>{' '}
                  segment{targetRatio !== 1 ? 's' : ''} at the same time.
                </p>
              </div>
            </div>
          )}

          {sourceCycle && targetCycle && (
            <div className="rounded-lg border border-border bg-muted/20 p-4 space-y-3">
              <Label className="text-xs text-muted-foreground uppercase tracking-wider">Link start points</Label>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1">
                  <Label className="text-xs text-muted-foreground">{sourceCycle.name} start</Label>
                  <Input
                    type="number"
                    min={1}
                    max={sourceCycle.stages}
                    step={1}
                    value={sourceStage}
                    onChange={(e) => setSourceStage(clampStageValue(Number(e.target.value || 1), sourceCycle.stages))}
                    className="bg-background font-mono text-center"
                  />
                  <p className="text-[11px] text-muted-foreground">
                    Stage 1 of {sourceCycle.stages}
                  </p>
                </div>
                <div className="space-y-1">
                  <Label className="text-xs text-muted-foreground">{targetCycle.name} start</Label>
                  <Input
                    type="number"
                    min={1}
                    max={targetCycle.stages}
                    step={1}
                    value={targetStage}
                    onChange={(e) => setTargetStage(clampStageValue(Number(e.target.value || 1), targetCycle.stages))}
                    className="bg-background font-mono text-center"
                  />
                  <p className="text-[11px] text-muted-foreground">
                    Stage 1 of {targetCycle.stages}
                  </p>
                </div>
              </div>
              <p className="text-xs text-muted-foreground">
                Choose which stage on each ring should align when applying the relationship.
              </p>
            </div>
          )}

          {/* Relationship Info */}
          {sourceCycle && targetCycle && (relationship || directLinkSummary) && (
            <div className="rounded-lg border border-border bg-muted/30 p-4 space-y-2">
              <div className="flex items-start gap-2">
                <Info className="w-4 h-4 text-primary mt-0.5 shrink-0" />
                <div className="space-y-1">
                  <div className="font-semibold text-foreground">
                    {directLinkSummary?.title ?? relationship?.description}
                  </div>
                  <div className="text-sm text-muted-foreground">
                    {directLinkSummary?.description ?? relationship?.detail}
                  </div>
                  {relationship && ruleRatio && sourceCycle && targetCycle && (
                    <div className="grid grid-cols-2 gap-2 pt-2 text-xs text-muted-foreground">
                      <div className="space-y-1">
                        <Label className="text-[10px] uppercase tracking-wider text-muted-foreground/80">
                          Rule: {sourceCycle.name}
                        </Label>
                        <div className="rounded-md border border-border bg-background/70 px-2 py-1 font-mono text-foreground">
                          {ruleRatio.source.toFixed(4)}
                        </div>
                      </div>
                      <div className="space-y-1">
                        <Label className="text-[10px] uppercase tracking-wider text-muted-foreground/80">
                          Rule: {targetCycle.name}
                        </Label>
                        <div className="rounded-md border border-border bg-background/70 px-2 py-1 font-mono text-foreground">
                          {ruleRatio.target.toFixed(4)}
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}
          {sourceCycle && targetCycle && !relationship && !directLinkSummary && (
            <div className="rounded-lg border border-border bg-muted/30 p-4 space-y-2 text-sm text-muted-foreground">
              <div className="flex items-start gap-2">
                <Info className="w-4 h-4 text-primary mt-0.5 shrink-0" />
                <div>
                  No preset tether found for these cycles. Use the segment ratio above to define how they should move together.
                </div>
              </div>
            </div>
          )}

          {/* Tether Chain */}
          {sourceCycle?.parentId && (
            <div className="text-xs text-muted-foreground">
              <span className="uppercase tracking-wider">Tether Chain: </span>
              <span className="flex items-center gap-1 mt-1 flex-wrap">
                {getHierarchyChain(sourceCycle).map((c, i, arr) => (
                  <span key={c.id} className="flex items-center gap-1">
                    <span className={c.id === sourceCycleId || c.id === targetCycleId ? 'text-primary font-medium' : ''}>
                      {c.name}
                    </span>
                    {i < arr.length - 1 && <ArrowRight className="w-3 h-3" />}
                  </span>
                ))}
              </span>
            </div>
          )}
        </div>
        
        <DialogFooter className="flex gap-2">
          <Button variant="ghost" onClick={() => { onClose(); resetForm(); }}>
            Close
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
